import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';

/** Router state key for context-aware back navigation. */
export const NAV_BACK_TO = 'backTo';

export interface NavigationBackState {
  backTo?: string;
  /** Äldre alias från t.ex. skaderapporter / blockerade bilar */
  from?: string;
}

export function withBackTo(backTo: string): { state: NavigationBackState } {
  return { state: { backTo } };
}

export function resolveBackTo(locationState: unknown, defaultRoute: string): string {
  const state = locationState as NavigationBackState | null;
  const backTo = state?.backTo || state?.from;
  return typeof backTo === 'string' && backTo.startsWith('/') ? backTo : defaultRoute;
}

const PREFIX_FALLBACKS: Array<{ prefix: string; fallback: string }> = [
  { prefix: '/analysis', fallback: '/system/analysis' },
  { prefix: '/reports', fallback: '/system/reports' },
  { prefix: '/admin', fallback: '/system/administration' },
  { prefix: '/booking', fallback: '/system/booking' },
  { prefix: '/work-orders', fallback: '/system/workorder' },
  { prefix: '/crm', fallback: ROUTES.CRM },
  { prefix: '/fleet', fallback: ROUTES.FLEET },
  { prefix: '/service', fallback: '/system/service' },
  { prefix: '/workshop', fallback: '/system/workshop' },
  { prefix: '/temperature-monitoring', fallback: '/system/temperature-monitoring' },
  { prefix: '/organization', fallback: ROUTES.HOME },
  { prefix: '/manual', fallback: ROUTES.HOME },
  { prefix: '/testing', fallback: ROUTES.HOME },
];

/** Säker fallback när historiken är tom — aldrig en route som saknas. */
export function inferFallbackRoute(pathname: string): string {
  if (pathname.startsWith('/system/')) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length >= 3) {
      return `/system/${parts[1]}`;
    }
    return ROUTES.HOME;
  }
  for (const { prefix, fallback } of PREFIX_FALLBACKS) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return fallback;
    }
  }
  return ROUTES.HOME;
}

export function canNavigateBack(): boolean {
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return typeof idx === 'number' && idx > 0;
}

export interface NavigateBackOptions {
  onBack?: () => void;
  backTo?: string;
  fallback?: string;
}

/**
 * Gå tillbaka till sidan vi kom ifrån.
 * 1) egen onBack (drill-down)
 * 2) explicit backTo / location.state.backTo|from
 * 3) historik om den finns i appen
 * 4) systemlandning för nuvarande modul — aldrig tom/vit sida
 */
export function useNavigateBack(explicitFallback?: string) {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (options?: NavigateBackOptions) => {
      if (options?.onBack) {
        options.onBack();
        return;
      }
      const explicit = options?.backTo;
      if (explicit && explicit.startsWith('/')) {
        navigate(explicit);
        return;
      }
      const fromState = resolveBackTo(location.state, '');
      if (fromState) {
        navigate(fromState);
        return;
      }
      if (canNavigateBack()) {
        navigate(-1);
        return;
      }
      navigate(options?.fallback || explicitFallback || inferFallbackRoute(location.pathname));
    },
    [explicitFallback, location.pathname, location.state, navigate]
  );
}
